import { Dialog, Transition } from '@headlessui/react';
import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { getAuthorizationHeader, http } from '../../config/http';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  IconRefresh,
  IconX,
  IconSearch,
  IconBox,
  IconChartPie,
  IconListDetails,
  IconPhoto,
  IconInbox,
} from '@tabler/icons-react';
import LoadingIndicator from '../LoadingIndicator';
import { useMasterData } from '../../context/MasterDataContext';
import RingkasanKategoriKarat from './Summary/RingkasanKategoriKarat';
import { resolveItemImageUrl } from '../../utils/itemImage';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

export default function PindahBakiBanyak({
  setOpenPindahBanyakBaki,
  refreshPage,
  reloadDate,
  openPindahBanyakBaki,
  setSelectedItems,
  selectedItems = [],
  resetSelectPindahItems,
  closeModalBanyakBarang,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const ids = selectedItems.map((item) => item.id);
  const [nomorRak, setNomorRak] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'list' | 'galeri'
  const [isOpenBakiModal, setIsOpenBakiModal] = useState(false);
  const [searchBaki, setSearchBaki] = useState('');
  const [searchItem, setSearchItem] = useState('');

  const { bakiList = [] } = useMasterData() || {};
  const bakiOption =
    bakiList && bakiList.length > 0
      ? bakiList
      : Array.from({ length: 20 }, (_, i) => i + 1);

  const filteredBakiOption = useMemo(() => {
    if (!searchBaki.trim()) return bakiOption;
    const q = searchBaki.toLowerCase().trim();
    return bakiOption.filter((opt) => {
      const str = String(opt).toLowerCase();
      return str.includes(q) || `baki ${str}`.includes(q);
    });
  }, [bakiOption, searchBaki]);

  // Reset tab and search on open/close
  useEffect(() => {
    if (!openPindahBanyakBaki) {
      setActiveTab('summary');
      setSearchItem('');
      setShowConfirm(false);
      setNomorRak('');
      setSearchBaki('');
    }
  }, [openPindahBanyakBaki]);

  // Statistics
  const totalPcs = selectedItems.length;
  const totalBerat = selectedItems.reduce(
    (acc, curr) => acc + (parseFloat(curr?.berat) || 0),
    0
  );

  const totalKategori = useMemo(() => {
    const cats = new Set(
      selectedItems.map(
        (i) =>
          i?.jenis_kategori?.kategori?.nama_kategori ||
          i?.kategori?.nama_kategori ||
          i?.item?.jenis_kategori?.kategori?.nama_kategori ||
          'Tanpa Kategori'
      )
    );
    return cats.size;
  }, [selectedItems]);

  const avgBerat = totalPcs > 0 ? totalBerat / totalPcs : 0;

  const validModalItems = useMemo(() => {
    return selectedItems.filter(
      (it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) > 0
    );
  }, [selectedItems]);

  const avgHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    const sumModal = validModalItems.reduce(
      (acc, it) => acc + Number(it?.harga_modal || it?.item?.harga_modal || 0),
      0
    );
    return Math.round(sumModal / validModalItems.length);
  }, [validModalItems]);

  const minBerat = useMemo(() => {
    const validWeights = selectedItems
      .map((it) => parseFloat(it?.berat || it?.item?.berat || 0) || 0)
      .filter((w) => w > 0);
    return validWeights.length > 0 ? Math.min(...validWeights) : 0;
  }, [selectedItems]);

  const maxBerat = useMemo(() => {
    if (selectedItems.length === 0) return 0;
    return Math.max(
      ...selectedItems.map((it) => parseFloat(it?.berat || it?.item?.berat || 0) || 0)
    );
  }, [selectedItems]);

  const minHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    return Math.min(
      ...validModalItems.map((it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) || 0)
    );
  }, [validModalItems]);

  const maxHargaModal = useMemo(() => {
    if (selectedItems.length === 0) return 0;
    return Math.max(
      ...selectedItems.map((it) => Number(it?.harga_modal || it?.item?.harga_modal || 0) || 0)
    );
  }, [selectedItems]);

  // Filtered items for List & Galeri
  const filteredItems = useMemo(() => {
    if (!searchItem.trim()) return selectedItems;
    const q = searchItem.toLowerCase().trim();
    return selectedItems.filter((item) => {
      const kode = String(item.kode_item || '').toLowerCase();
      const nama = String(
        item.jenis_kategori?.nama_jenis || item.kategori?.nama_kategori || ''
      ).toLowerCase();
      const baki = String(item.nomor_rak || '').toLowerCase();
      return kode.includes(q) || nama.includes(q) || baki.includes(q);
    });
  }, [selectedItems, searchItem]);

  async function handleSubmitPindahBaki(e) {
    if (e) e.preventDefault();
    if (!nomorRak) {
      toast.error('Pilih nomor baki tujuan terlebih dahulu!', { autoClose: 1500 });
      return;
    }

    setLoading(true);
    const formData = {
      items: ids,
      nomor_rak: nomorRak,
    };

    try {
      const response = await http.post('/item/pindah_baki', formData, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      toast.success('BAKI berhasil diperbarui', { autoClose: 1000 });
      setOpenPindahBanyakBaki(false);
      setShowConfirm(false);
      setNomorRak('');
      if (reloadDate) reloadDate();
      setLoading(false);
      if (resetSelectPindahItems) resetSelectPindahItems();
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Gagal memperbarui BAKI', {
        autoClose: 2000,
      });
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }

  function removeSelectedItem(itemId) {
    if (setSelectedItems) {
      setSelectedItems((currentItems) =>
        currentItems.filter((item) => item.id !== itemId)
      );
    }
  }

  return (
    <>
      {loading && <LoadingIndicator />}
      <Transition appear show={openPindahBanyakBaki} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[99999]"
          onClose={loading ? () => {} : closeModalBanyakBarang}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95 translate-y-2"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-2"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col max-h-[90vh]">
                  {/* Header */}
                  <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between sticky top-0 z-20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        <IconBox size={22} />
                      </div>
                      <div>
                        <Dialog.Title
                          as="h2"
                          className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100"
                        >
                          Pindah ke BAKI Lain
                        </Dialog.Title>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Total:{' '}
                          <span className="font-semibold text-slate-700 dark:text-slate-300">
                            {totalPcs} Barang | {totalBerat.toFixed(3)} gr
                          </span>
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeModalBanyakBarang}
                      disabled={loading}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                    >
                      <IconX size={18} />
                    </button>
                  </div>

                  {/* Navigation Tab Bar */}
                  <div className="flex border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 sticky top-[61px] z-20">
                    <button
                      type="button"
                      onClick={() => setActiveTab('summary')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'summary'
                          ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconChartPie size={16} />
                      <span>Rangkuman Item</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('list')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'list'
                          ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconListDetails size={16} />
                      <span>List Item ({totalPcs})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('galeri')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'galeri'
                          ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconPhoto size={16} />
                      <span>Galeri List Item</span>
                    </button>
                  </div>

                  {/* Tab Content Body */}
                  <div className="p-5 flex-1 bg-slate-50/50 dark:bg-slate-900/50 overflow-y-auto min-h-[350px]">
                    {/* 1. TAB RANGKUMAN ITEM */}
                    {activeTab === 'summary' && (
                      <div className="space-y-4">
                        {/* 9 Stat Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
                          <div className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/70 dark:border-emerald-900/40 rounded-xl p-3">
                            <span className="text-emerald-700 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider block">
                              Total Barang
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                              {totalPcs}{' '}
                              <span className="text-xs font-semibold text-slate-500">
                                Pcs
                              </span>
                            </div>
                          </div>
                          <div className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/70 dark:border-emerald-900/40 rounded-xl p-3">
                            <span className="text-emerald-700 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider block">
                              Total Berat
                            </span>
                            <div className="text-lg font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 font-mono">
                              {totalBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">
                                gr
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Total Kategori
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                              {totalKategori}{' '}
                              <span className="text-xs font-semibold text-slate-500">
                                Kategori
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Rata-rata Berat
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {avgBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">
                                gr
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Berat Minimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {minBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">
                                gr
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Berat Maksimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {maxBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">
                                gr
                              </span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Rata-rata Modal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {avgHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Modal Minimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {minHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Modal Maksimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {maxHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                        </div>

                        {/* Breakdown Kategori & Karat */}
                        <RingkasanKategoriKarat selectedItems={selectedItems} />

                        {/* Pilihan Baki Tujuan Card */}
                        <div className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2">
                          <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                            Baki Tujuan <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setIsOpenBakiModal(true)}
                              className="flex-1 h-11 border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 px-4 text-xs rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer shadow-xs transition flex items-center justify-between text-left"
                            >
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-xs">
                                  <IconBox size={16} />
                                </div>
                                <span
                                  className={
                                    nomorRak
                                      ? 'font-bold text-slate-800 dark:text-slate-100'
                                      : 'text-slate-400 dark:text-slate-400'
                                  }
                                >
                                  {nomorRak
                                    ? `Baki ${nomorRak}`
                                    : 'Pilih Baki Tujuan Pemindahan...'}
                                </span>
                              </div>
                              <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold text-[11px]">
                                {nomorRak ? 'Ganti Baki' : 'Pilih Baki'}
                              </span>
                            </button>
                            {nomorRak && (
                              <button
                                type="button"
                                onClick={() => setNomorRak('')}
                                className="h-11 px-3 rounded-xl border border-slate-200 dark:border-slate-700 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-semibold transition"
                                title="Reset Baki"
                              >
                                Reset
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 italic">
                            Semua barang yang dipilih akan diperbarui nomor bakinya ke baki tujuan yang dipilih.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* 2. TAB LIST ITEM */}
                    {activeTab === 'list' && (
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <IconSearch
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                              size={15}
                            />
                            <input
                              type="text"
                              placeholder="Cari kode, jenis, baki..."
                              value={searchItem}
                              onChange={(e) => setSearchItem(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200"
                            />
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            Menampilkan {filteredItems.length} dari {totalPcs} item
                          </span>
                        </div>

                        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-[380px] bg-white dark:bg-slate-900">
                          <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                            <thead className="bg-slate-50 dark:bg-slate-800/80 uppercase border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 font-bold tracking-wider text-[11px] text-slate-500 dark:text-slate-400">
                              <tr>
                                <th className="p-2.5 w-10 text-center">No</th>
                                <th className="p-2.5">Kode Barang</th>
                                <th className="p-2.5">Nama Barang</th>
                                <th className="p-2.5 text-center">Baki Asal</th>
                                <th className="p-2.5 text-right">Berat (gr)</th>
                                <th className="p-2.5 text-center">Karat</th>
                                <th className="p-2.5 text-center w-16">Batal</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {filteredItems.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={7}
                                    className="text-center py-8 text-slate-400"
                                  >
                                    Tidak ada item yang sesuai dengan pencarian
                                  </td>
                                </tr>
                              ) : (
                                filteredItems.map((item, idx) => (
                                  <tr
                                    key={item.id || idx}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                  >
                                    <td className="p-2.5 text-slate-500 font-medium text-center">
                                      {idx + 1}.
                                    </td>
                                    <td className="p-2.5 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                      {item.kode_item}
                                    </td>
                                    <td className="p-2.5 font-medium text-slate-700 dark:text-slate-200">
                                      {item.jenis_kategori?.nama_jenis ||
                                        item.kategori?.nama_kategori ||
                                        '-'}
                                    </td>
                                    <td className="p-2.5 text-center font-medium">
                                      {item.nomor_rak || '-'}
                                    </td>
                                    <td className="p-2.5 text-right font-bold text-slate-800 dark:text-slate-100 font-mono">
                                      {parseFloat(item.berat || 0).toFixed(3)} gr
                                    </td>
                                    <td className="p-2.5 text-center font-semibold text-amber-600 dark:text-amber-400">
                                      {item.karat} K
                                    </td>
                                    <td className="p-2.5 text-center">
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedItem(item.id)}
                                        disabled={loading || selectedItems.length <= 1}
                                        className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                        title={
                                          selectedItems.length <= 1
                                            ? 'Minimal 1 barang'
                                            : 'Batal Pilih'
                                        }
                                      >
                                        <IconX size={15} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                            <tfoot className="bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 font-bold sticky bottom-0 z-10">
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-2.5 text-right text-slate-700 dark:text-slate-300"
                                >
                                  Total Berat:
                                </td>
                                <td className="p-2.5 text-right text-emerald-600 dark:text-emerald-400 font-mono">
                                  {totalBerat.toFixed(3)} gr
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 3. TAB GALERI LIST ITEM */}
                    {activeTab === 'galeri' && (
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <IconSearch
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                              size={15}
                            />
                            <input
                              type="text"
                              placeholder="Cari kode, jenis, baki..."
                              value={searchItem}
                              onChange={(e) => setSearchItem(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200"
                            />
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            Menampilkan {filteredItems.length} foto item
                          </span>
                        </div>

                        {filteredItems.length === 0 ? (
                          <div className="py-12 text-center text-slate-400">
                            <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                            <p className="text-xs">
                              Tidak ada item yang sesuai dengan pencarian
                            </p>
                          </div>
                        ) : (
                          <PhotoProvider>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[380px] overflow-y-auto pr-1">
                              {filteredItems.map((item) => {
                                const imgUrl = resolveItemImageUrl(item);
                                return (
                                  <div
                                    key={item.id || item.kode_item}
                                    className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col relative"
                                  >
                                    <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                      {imgUrl ? (
                                        <PhotoView src={imgUrl}>
                                          <img
                                            src={imgUrl}
                                            alt={item.kode_item}
                                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = '';
                                            }}
                                          />
                                        </PhotoView>
                                      ) : (
                                        <div className="flex flex-col items-center justify-center text-slate-400 p-4 text-center">
                                          <IconPhoto
                                            size={28}
                                            className="opacity-40 mb-1"
                                          />
                                          <span className="text-[10px]">
                                            Tidak ada foto
                                          </span>
                                        </div>
                                      )}
                                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-xs text-white text-[10px] font-mono font-bold">
                                        {item.karat}K
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedItem(item.id)}
                                        disabled={loading || selectedItems.length <= 1}
                                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center transition opacity-80 hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={
                                          selectedItems.length <= 1
                                            ? 'Minimal 1 barang'
                                            : 'Batal Pilih'
                                        }
                                      >
                                        <IconX size={12} />
                                      </button>
                                    </div>

                                    <div className="p-2.5 flex-1 flex flex-col justify-between text-xs">
                                      <div>
                                        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 block truncate">
                                          {item.kode_item}
                                        </span>
                                        <span className="text-slate-600 dark:text-slate-300 font-medium block truncate text-[11px]">
                                          {item.jenis_kategori?.nama_jenis ||
                                            item.kategori?.nama_kategori ||
                                            '-'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100 dark:border-slate-700 text-[11px]">
                                        <span className="text-slate-400">
                                          Asal: {item.nomor_rak || '-'}
                                        </span>
                                        <span className="font-bold text-slate-800 dark:text-slate-200">
                                          {parseFloat(item.berat || 0).toFixed(3)} gr
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </PhotoProvider>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 sticky bottom-0 z-20">
                    <div className="relative flex-1 min-w-0 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={() => setIsOpenBakiModal(true)}
                        className="w-full sm:max-w-xs h-9 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-xs rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/50 cursor-pointer shadow-xs dark:text-slate-200 flex items-center justify-between text-left"
                      >
                        <span
                          className={
                            nomorRak
                              ? 'font-bold text-emerald-600 dark:text-emerald-400 truncate'
                              : 'text-slate-400 dark:text-slate-400 truncate'
                          }
                        >
                          {nomorRak ? `Baki Tujuan: ${nomorRak}` : 'Pilih Baki Tujuan...'}
                        </span>
                        <IconSearch size={14} className="text-slate-400 shrink-0 ml-2" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
                      <button
                        type="button"
                        onClick={closeModalBanyakBarang}
                        disabled={loading}
                        className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                      >
                        Batal
                      </button>
                      <button
                        type="button"
                        disabled={
                          loading || selectedItems.length === 0 || !nomorRak
                        }
                        onClick={() => setShowConfirm(true)}
                        className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white text-xs font-bold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                      >
                        <IconRefresh size={16} />
                        <span>Update Baki</span>
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Dialog */}
      <Transition appear show={showConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100000]"
          onClose={() => !loading && setShowConfirm(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 p-6 text-left align-middle shadow-2xl transition-all border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-center mb-4 w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 mx-auto">
                    <IconRefresh className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-base font-bold text-slate-900 dark:text-slate-100 text-center"
                  >
                    Konfirmasi Update Baki
                  </Dialog.Title>
                  <div className="mt-2 text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      Anda yakin akan memindahkan{' '}
                      <span className="font-bold text-slate-700 dark:text-slate-300">
                        {selectedItems.length}
                      </span>{' '}
                      barang ini ke{' '}
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        Baki {nomorRak}
                      </span>
                      ?
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => setShowConfirm(false)}
                      className="w-full inline-flex justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleSubmitPindahBaki}
                      className="w-full inline-flex justify-center rounded-xl bg-emerald-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-emerald-600 shadow-md transition"
                    >
                      {loading ? 'Memproses...' : 'Ya, Update'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal Pilih Baki Tujuan */}
      <Transition appear show={isOpenBakiModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100001]"
          onClose={() => {
            setIsOpenBakiModal(false);
            setSearchBaki('');
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col max-h-[80vh] text-left align-middle transition-all">
                  {/* Header */}
                  <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-xs">
                        <IconBox size={18} />
                      </div>
                      <div>
                        <Dialog.Title
                          as="h3"
                          className="font-bold text-sm text-slate-800 dark:text-slate-100"
                        >
                          Pilih Baki Tujuan
                        </Dialog.Title>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Pilih baki tujuan pemindahan barang
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpenBakiModal(false);
                        setSearchBaki('');
                      }}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    >
                      <IconX size={18} />
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                    <div className="relative">
                      <IconSearch
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        placeholder="Cari nomor baki (misal: 1, 5, 12)..."
                        value={searchBaki}
                        onChange={(e) => setSearchBaki(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Options List */}
                  <div className="overflow-y-auto p-3 max-h-[50vh]">
                    {filteredBakiOption.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {filteredBakiOption.map((opt) => {
                          const isSelected = String(nomorRak) === String(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                setNomorRak(opt);
                                setIsOpenBakiModal(false);
                                setSearchBaki('');
                              }}
                              className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                                isSelected
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 font-bold shadow-sm'
                                  : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-slate-700/50 font-medium'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <div
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs ${
                                    isSelected
                                      ? 'bg-emerald-500 text-white shadow-sm'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                  }`}
                                >
                                  {opt}
                                </div>
                                <span className="text-xs">Baki {opt}</span>
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-sm text-slate-500 dark:text-slate-400">
                        Nomor baki tidak ditemukan
                      </div>
                    )}
                  </div>

                  {/* Selected Indicator Footer */}
                  {nomorRak && (
                    <div className="p-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        Terpilih:{' '}
                        <strong className="text-emerald-600 dark:text-emerald-400 font-bold">
                          Baki {nomorRak}
                        </strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setNomorRak('');
                          setSearchBaki('');
                        }}
                        className="text-rose-500 hover:text-rose-700 font-semibold cursor-pointer"
                      >
                        Reset Pilihan
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
